/**
 * React phase — Tinder-style swipe deck of ideas.
 *
 * Gestures:
 *   →  swipe right  →  Love
 *   ←  swipe left   →  Pass
 *   ↑  swipe up     →  Curious (the "star")
 *
 * Inputs: pointer events (covers mouse + touch + stylus). No external
 * gesture library — kept hand-rolled to avoid an extra dep at this scale.
 *
 * Each swipe POSTs the reaction and pops the card off the stack. Buttons
 * underneath act as accessible fallbacks (and let you keep one finger on
 * the screen instead of swiping).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import { UiNode } from '../lib/UiNode';
import { IconHeart, IconStar, IconX, IconArrowLeft } from '../components/Icon';
import { Avatar } from '../components/Avatar';
import type { BoardDetail, IdeaWithReactions, ReactionKind } from '../../shared/types';

const SWIPE_THRESHOLD = 90;
const VELOCITY_FLICK = 0.45; // px/ms — overrides distance threshold

export function ReactPhase({
  board,
  ideas,
}: {
  board: BoardDetail;
  ideas: IdeaWithReactions[];
}) {
  // Stack order is stable-ish — sort by createdAt so everyone sees the
  // same order. We use updatedReactions for live counts displayed at the
  // end-of-deck recap, but it doesn't influence stack order.
  const stack = useMemo(
    () => [...ideas].sort((a, b) => a.createdAt - b.createdAt),
    [ideas],
  );
  const [index, setIndex] = useState(0);
  // Track decisions so we can let the user undo the last swipe.
  const [history, setHistory] = useState<Array<{ ideaId: string; was: ReactionKind | null; now: ReactionKind | null }>>([]);

  // When the upstream list grows (new ideas added live during React),
  // keep the user's place by not resetting the index. New ideas appear
  // at the bottom of the stack.
  useEffect(() => {
    if (index > stack.length) setIndex(stack.length);
  }, [stack.length, index]);

  const current = stack[index];
  const upcoming = stack.slice(index + 1, index + 3);

  function applyReaction(idea: IdeaWithReactions, kind: ReactionKind): void {
    setHistory((h) => [...h, { ideaId: idea.id, was: idea.myReaction, now: kind }]);
    void api.react(board.id, idea.id, kind);
    setIndex((i) => i + 1);
  }

  function undo(): void {
    if (history.length === 0) return;
    const last = history[history.length - 1]!;
    setHistory((h) => h.slice(0, -1));
    setIndex((i) => Math.max(0, i - 1));
    void api.react(board.id, last.ideaId, last.was);
  }

  if (stack.length === 0) {
    return (
      <div className="panel flex flex-col items-center gap-2 p-10 text-center text-cream-3">
        <p className="text-sm">No ideas yet — switch back to Capture.</p>
      </div>
    );
  }

  if (!current) {
    return <Recap stack={stack} board={board} onReset={() => setIndex(0)} />;
  }

  return (
    <div className="flex flex-col items-center gap-6 pb-6">
      <p className="text-center text-xs text-cream-3">
        {index + 1} of {stack.length} · swipe or tap
      </p>

      <div className="relative h-[440px] w-full max-w-sm">
        {upcoming
          .slice()
          .reverse()
          .map((idea, n) => {
            const depth = upcoming.length - n; // 1, 2 from front
            return (
              <div
                key={idea.id}
                className="absolute inset-0 rounded-2xl border border-ink-rim/60 bg-ink-2 shadow-lg"
                style={{
                  transform: `translateY(${depth * 8}px) scale(${1 - depth * 0.04})`,
                  opacity: 1 - depth * 0.2,
                  zIndex: -depth,
                }}
              />
            );
          })}
        <SwipeCard
          key={current.id}
          idea={current}
          board={board}
          onSwipe={(kind) => applyReaction(current, kind)}
        />
      </div>

      <div className="flex items-center gap-3">
        <ActionButton
          color="rust"
          label="Pass"
          icon={<IconX size={20} />}
          onClick={() => applyReaction(current, 'pass')}
        />
        <ActionButton
          color="amber"
          label="Curious"
          icon={<IconStar size={20} />}
          onClick={() => applyReaction(current, 'curious')}
        />
        <ActionButton
          color="leaf"
          label="Love"
          icon={<IconHeart size={20} />}
          onClick={() => applyReaction(current, 'love')}
        />
      </div>
      <button
        type="button"
        className="btn btn-ghost inline-flex items-center gap-1.5 text-xs"
        onClick={undo}
        disabled={history.length === 0}
      >
        <IconArrowLeft size={12} />
        Undo last
      </button>
    </div>
  );
}

interface DragState {
  dx: number;
  dy: number;
  startX: number;
  startY: number;
  startT: number;
  pointerId: number;
  flying: ReactionKind | null;
}

function SwipeCard({
  idea,
  board,
  onSwipe,
}: {
  idea: IdeaWithReactions;
  board: BoardDetail;
  onSwipe: (kind: ReactionKind) => void;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const [tick, setTick] = useState(0); // re-render on drag move
  const author = board.members.find((m) => m.id === idea.authorId);

  function setStyle(dx: number, dy: number, flying: ReactionKind | null): void {
    const el = cardRef.current;
    if (!el) return;
    if (flying) {
      const x = flying === 'pass' ? -window.innerWidth : flying === 'love' ? window.innerWidth : 0;
      const y = flying === 'curious' ? -window.innerHeight : dy + Math.sign(dy) * 200;
      const rot = flying === 'pass' ? -30 : flying === 'love' ? 30 : 0;
      el.style.transition = 'transform 380ms cubic-bezier(.22,.61,.36,1), opacity 380ms';
      el.style.transform = `translate(${x}px, ${y}px) rotate(${rot}deg)`;
      el.style.opacity = '0';
      return;
    }
    el.style.transition = dragRef.current ? 'none' : 'transform 220ms ease, opacity 220ms';
    const rot = dx / 16;
    el.style.transform = `translate(${dx}px, ${dy}px) rotate(${rot}deg)`;
    el.style.opacity = '1';
  }

  function decideOrSnap(): void {
    const s = dragRef.current;
    if (!s) return;
    const dt = Math.max(1, performance.now() - s.startT);
    const vx = s.dx / dt;
    const vy = s.dy / dt;
    let kind: ReactionKind | null = null;
    if (s.dy < -SWIPE_THRESHOLD || vy < -VELOCITY_FLICK) kind = 'curious';
    else if (s.dx > SWIPE_THRESHOLD || vx > VELOCITY_FLICK) kind = 'love';
    else if (s.dx < -SWIPE_THRESHOLD || vx < -VELOCITY_FLICK) kind = 'pass';
    if (kind) {
      s.flying = kind;
      setStyle(s.dx, s.dy, kind);
      setTimeout(() => onSwipe(kind!), 360);
    } else {
      dragRef.current = null;
      setStyle(0, 0, null);
      setTick((t) => t + 1);
    }
  }

  function onPointerDown(e: React.PointerEvent): void {
    if (dragRef.current) return;
    const el = cardRef.current;
    if (!el) return;
    el.setPointerCapture(e.pointerId);
    dragRef.current = {
      dx: 0,
      dy: 0,
      startX: e.clientX,
      startY: e.clientY,
      startT: performance.now(),
      pointerId: e.pointerId,
      flying: null,
    };
  }
  function onPointerMove(e: React.PointerEvent): void {
    const s = dragRef.current;
    if (!s || e.pointerId !== s.pointerId || s.flying) return;
    s.dx = e.clientX - s.startX;
    s.dy = e.clientY - s.startY;
    setStyle(s.dx, s.dy, null);
    setTick((t) => t + 1);
  }
  function onPointerUp(e: React.PointerEvent): void {
    const s = dragRef.current;
    if (!s || e.pointerId !== s.pointerId || s.flying) return;
    decideOrSnap();
  }

  // Compute hint overlay based on current drag.
  const s = dragRef.current;
  const hint = useMemo(() => {
    if (!s) return null;
    if (s.dy < -50 && Math.abs(s.dx) < Math.abs(s.dy))
      return { kind: 'curious' as const, intensity: Math.min(1, Math.abs(s.dy) / SWIPE_THRESHOLD) };
    if (s.dx > 30)
      return { kind: 'love' as const, intensity: Math.min(1, s.dx / SWIPE_THRESHOLD) };
    if (s.dx < -30)
      return { kind: 'pass' as const, intensity: Math.min(1, -s.dx / SWIPE_THRESHOLD) };
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  return (
    <UiNode
      as="article"
      name="react-idea-card"
      entity="idea"
      entityId={idea.id}
      className="absolute inset-0 flex cursor-grab touch-none select-none flex-col gap-3 rounded-2xl border p-5 shadow-2xl active:cursor-grabbing"
      style={
        idea.colorHue != null
          ? {
              borderColor: `hsla(${idea.colorHue}, 70%, 55%, 0.7)`,
              background: `linear-gradient(135deg, hsla(${idea.colorHue}, 55%, 22%, 0.55), var(--color-ink-2) 60%)`,
            }
          : { borderColor: 'var(--color-ink-rim)', background: 'var(--color-ink-2)' }
      }
      ref={cardRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {hint && <SwipeHint hint={hint.kind} intensity={hint.intensity} />}

      <header className="flex items-center gap-2">
        {author && <Avatar user={author} size={28} />}
        <div className="min-w-0">
          {author && <p className="truncate text-xs text-cream-3">{author.name}</p>}
        </div>
      </header>

      {idea.emoji && (
        <div
          aria-hidden
          className="mx-auto text-6xl leading-none"
          style={
            idea.colorHue != null
              ? { filter: `drop-shadow(0 4px 18px hsla(${idea.colorHue}, 80%, 55%, 0.55))` }
              : undefined
          }
        >
          {idea.emoji}
        </div>
      )}
      <p className="text-xl leading-snug">{idea.text}</p>

      {idea.note && (
        <p className="text-sm text-cream-3">{idea.note}</p>
      )}

      {idea.attachments.length > 0 && (
        <div className="mt-auto flex flex-col gap-2">
          {idea.attachments
            .filter((a) => a.kind === 'image' && a.imageUrl)
            .slice(0, 1)
            .map((a) => (
              <img
                key={a.id}
                src={a.imageUrl!}
                alt=""
                className="max-h-44 w-full rounded-lg object-cover"
                draggable={false}
              />
            ))}
          {idea.attachments
            .filter((a) => a.kind === 'link')
            .slice(0, 1)
            .map((a) => (
              <div key={a.id} className="rounded-lg border border-ink-rim/60 p-2 text-xs text-cream-3">
                {a.title ?? a.url}
              </div>
            ))}
        </div>
      )}
    </UiNode>
  );
}

function SwipeHint({ hint, intensity }: { hint: ReactionKind; intensity: number }) {
  const config = {
    love: { color: 'hsl(140 60% 55%)', label: 'LOVE', rot: -16 },
    pass: { color: 'hsl(0 70% 60%)', label: 'PASS', rot: 16 },
    curious: { color: 'hsl(42 90% 60%)', label: 'CURIOUS', rot: 0 },
  }[hint];
  return (
    <div
      className="pointer-events-none absolute inset-0 flex items-start justify-center pt-6"
      style={{ opacity: intensity }}
    >
      <span
        className="rounded-md border-4 px-3 py-1 text-2xl font-extrabold uppercase tracking-widest"
        style={{
          color: config.color,
          borderColor: config.color,
          transform: `rotate(${config.rot}deg)`,
        }}
      >
        {config.label}
      </span>
    </div>
  );
}

function ActionButton({
  color,
  label,
  icon,
  onClick,
}: {
  color: 'rust' | 'leaf' | 'amber';
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <UiNode
      as="button"
      name="react-reaction-button"
      entity="reaction"
      action="toggle"
      state="inactive"
      label={label}
      className="flex h-12 w-12 items-center justify-center rounded-full border-2"
      style={{
        borderColor:
          color === 'rust'
            ? 'hsl(0 70% 60%)'
            : color === 'leaf'
              ? 'hsl(140 60% 55%)'
              : 'hsl(42 90% 60%)',
        color:
          color === 'rust'
            ? 'hsl(0 70% 60%)'
            : color === 'leaf'
              ? 'hsl(140 60% 55%)'
              : 'hsl(42 90% 60%)',
      }}
      onClick={onClick}
      title={label}
    >
      {icon}
    </UiNode>
  );
}

function Recap({
  stack,
  board,
  onReset,
}: {
  stack: IdeaWithReactions[];
  board: BoardDetail;
  onReset: () => void;
}) {
  void board;
  const top = [...stack]
    .sort((a, b) => b.reactions.love + b.reactions.curious - (a.reactions.love + a.reactions.curious))
    .slice(0, 5);
  return (
    <div className="flex flex-col gap-5">
      <div className="panel flex flex-col items-center gap-2 p-8 text-center">
        <p className="text-base">You're through the deck.</p>
        <p className="text-sm text-cream-3">Move to Theme when the team's caught up.</p>
        <button type="button" className="btn btn-ghost mt-2" onClick={onReset}>
          Run through again
        </button>
      </div>
      <section className="panel p-4">
        <h2 className="text-base">Most loved</h2>
        <ul className="mt-2 flex flex-col gap-2">
          {top.map((i) => (
            <li key={i.id} className="flex items-baseline gap-3">
              <span className="text-sm">{i.text}</span>
              <span className="ml-auto text-xs text-cream-3">
                {i.reactions.love} love · {i.reactions.curious} curious · {i.reactions.pass} pass
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
